import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {PatientContactsDetails} from '@/features/patients/components/patient-contacts-details/PatientContactsDetails'
import {PatientContactsFields} from '@/features/patients/components/patient-contacts-fields/PatientContactsFields'
import {PatientDetailsEditButtons} from '@/features/patients/components/patient-details-edit-buttons/PatientDetailsEditButtons'
import {usePatientEditModeView} from '@/features/patients/hooks/usePatientEditModeView'
import {usePatientFormSubmission} from '@/features/patients/hooks/usePatientFormSubmission'
import {useUpdatePatient} from '@/features/patients/services/useUpdatePatient'
import {usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {PatientContactsFormValidationSchema, PatientExtended} from '@/features/patients/types'
import {
    getPatientDirtyFieldsFromFormValues,
    remapPatientToDefaultValues,
    remapPatientToPayload
} from '@/features/patients/utils'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {zodResolver} from '@hookform/resolvers/zod'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {useOutletContext} from 'react-router-dom'
import {usePermissions} from '@/features/auth/store/store'

export const PatientDetailsContactsSection = () => {
    const {patient} = useOutletContext<{patient: PatientExtended}>()
    const form = useForm<PatientContactsFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(PatientContactsFormValidationSchema),
        defaultValues: remapPatientToDefaultValues(patient)
    })

    return (
        <FormProvider {...form}>
            <PatientDetailsContactsContent patient={patient} />
        </FormProvider>
    )
}

const PatientDetailsContactsContent = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = usePatientEditModeView('contacts')
    const permissions = usePermissions()
    const setIsErrorBannerVisible = usePatientDetailsStore(state => state.setIsErrorBannerVisible)
    const form = useFormContext<PatientContactsFormValidationSchema>()
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = usePatientFormSubmission({
        formSchema: PatientContactsFormValidationSchema
    })

    // mutation
    const updatePatient = useUpdatePatient({
        urlParams: {patientId: patient.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getPatientDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapPatientToPayload(dirtyFormValues)
            return updatePatient.mutate(parsedValues)
        },
        errors => {
            setIsErrorBannerVisible(true)
            console.error(errors)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('commons:contacts')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.patients?.write && (
                    <PatientDetailsEditButtons
                        groupName="contacts"
                        isConfirmDisabled={ObjectKeys(form.formState.dirtyFields).length == 0}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                    />
                )
            }
        >
            {isEditModeEnabled ? <PatientContactsFields /> : <PatientContactsDetails patient={patient} />}
        </DetailsFieldsSection>
    )
}
